import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  ApiError,
  apiDownloadPlanningImportTemplate,
  apiRequest
} from "../../api/client";
import {
  parsePlanningWorkbook,
  PlanningWorkbookError,
  type PlanningWorkbookCatalog
} from "./planningWorkbook";

interface PlanningImportResult {
  created_requirement_count: number;
  created_allocation_count: number;
  skipped_requirement_count: number;
  skipped_allocation_count: number;
}

export function PlanningImportActions({
  projectId,
  conditionRevision,
  locked,
  catalog
}: {
  projectId: string;
  conditionRevision: number;
  locked: boolean;
  catalog: PlanningWorkbookCatalog;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<
    { tone: "success" | "error"; message: string } | null
  >(null);
  const [downloading, setDownloading] = useState(false);
  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const workbook = await parsePlanningWorkbook(file, catalog);
      return apiRequest<PlanningImportResult>(
        `/projects/${projectId}/planning/imports`,
        {
          method: "POST",
          body: JSON.stringify({
            ...workbook,
            expected_condition_revision: conditionRevision
          })
        }
      );
    },
    onSuccess: async (result) => {
      const skipped =
        result.skipped_requirement_count + result.skipped_allocation_count;
      setFeedback({
        tone: "success",
        message:
          `已导入 ${result.created_requirement_count} 条班级课程需求、` +
          `${result.created_allocation_count} 条教师任课分配` +
          (skipped ? `；${skipped} 条重复记录已跳过` : "")
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["planning-draft", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      ]);
      await queryClient.refetchQueries({
        queryKey: ["planning-draft", projectId],
        exact: true,
        type: "active"
      });
    },
    onError: async (error) => {
      setFeedback({ tone: "error", message: planningImportErrorMessage(error) });
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({
          queryKey: ["planning-draft", projectId]
        });
      }
    }
  });

  async function downloadTemplate(): Promise<void> {
    setFeedback(null);
    setDownloading(true);
    try {
      await apiDownloadPlanningImportTemplate(projectId);
    } catch (error) {
      setFeedback({
        tone: "error",
        message: error instanceof Error ? error.message : "模板下载失败"
      });
    } finally {
      setDownloading(false);
    }
  }

  async function selectFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setFeedback(null);
    await mutation.mutateAsync(file).catch(() => undefined);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <section className="planning-import-bar" aria-label="课程需求与教师分配导入">
      <div>
        <strong>一个模板导入课程需求与教师分配</strong>
        <span>模板已绑定当前项目的班级、课程、教师、功能教室和节次下拉选项。</span>
      </div>
      <div className="planning-import-buttons">
        <button
          className="ghost-button"
          type="button"
          disabled={downloading}
          onClick={() => void downloadTemplate()}
        >
          {downloading ? "正在生成…" : "下载合并导入模板"}
        </button>
        <button
          className="ghost-button"
          type="button"
          disabled={locked || mutation.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {mutation.isPending ? "正在导入…" : "导入课程需求与教师分配"}
        </button>
        <input
          ref={inputRef}
          className="visually-hidden"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-label="选择课程需求与教师分配 Excel 文件"
          disabled={locked || mutation.isPending}
          onChange={(event) => void selectFile(event.target.files?.[0])}
        />
      </div>
      {feedback ? (
        <p
          className={`planning-import-feedback ${feedback.tone}`}
          role={feedback.tone === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      ) : null}
    </section>
  );
}

function planningImportErrorMessage(error: unknown): string {
  if (error instanceof PlanningWorkbookError) {
    return locationMessage(error.message, {
      sheetName: error.sheetName,
      sourceRow: error.sourceRow,
      field: error.field
    });
  }
  if (error instanceof ApiError) {
    const details = error.details as Record<string, unknown> | undefined;
    const first = Array.isArray(details?.row_errors) ? details.row_errors[0] : undefined;
    if (isRecord(first)) {
      return locationMessage(String(first.message ?? error.message), {
        sheetName: typeof first.sheet_name === "string" ? first.sheet_name : undefined,
        sourceRow: typeof first.source_row === "number" ? first.source_row : undefined,
        field: typeof first.field === "string" ? first.field : undefined
      });
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "导入失败，请检查文件后重试";
}

function locationMessage(
  message: string,
  location: { sheetName?: string; sourceRow?: number; field?: string }
): string {
  const parts = [
    location.sheetName ? `工作表“${location.sheetName}”` : "",
    location.sourceRow ? `第 ${location.sourceRow} 行` : "",
    location.field ? `字段“${location.field}”` : ""
  ].filter(Boolean);
  return parts.length ? `${parts.join("，")}：${message}` : message;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
